import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Checkbox, Grid, Group, Select, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { Clock, FlaskConical, Search, Square, TerminalSquare } from 'lucide-react';
import { CATEGORY_LABEL, DIFFICULTY_ORDER, categoryLabel, toolLabel } from '../components/lab/labMeta';
import { motion } from 'framer-motion';
import { useDeferredValue, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageContainer,
  PageHeader,
  Skeleton,
} from '../components/ui';
import { staggerContainer } from '../lib/motion';
import { LabRow } from '../components/lab/LabRow';
import { useMyAssignments } from '../hooks/useGradebook';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { useLabs, useMyLabs, useStopLab } from '../hooks/useLabs';
import { useNow } from '../hooks/useNow';
import type { Difficulty, LabSummary } from '../api/types';

const DIFFICULTY_CHIPS: ReadonlyArray<{ key: Difficulty; label: string }> = [
  { key: 'beginner', label: 'Easy' },
  { key: 'intermediate', label: 'Medium' },
  { key: 'advanced', label: 'Hard' },
  { key: 'expert', label: 'Expert' },
];

export const Route = createFileRoute('/labs/')({
  component: LabsIndex,
});

function ActiveLabBanner() {
  const { data } = useMyLabs();
  const stopLab = useStopLab();
  const navigate = useNavigate();
  const [confirmStop, setConfirmStop] = useState(false);
  // Ticking clock — this readout used to freeze at mount, which is exactly the
  // number a trainee needs to trust.
  const now = useNow();
  const active = data?.active;
  if (!active) return null;
  const minsLeft = active.expires_at
    ? Math.max(0, Math.round((new Date(active.expires_at).getTime() - now) / 60000))
    : null;
  // Backend now persists the real lifecycle (provisioning → running → ready), so
  // the banner reflects each state instead of collapsing running into 'Provisioning'.
  const statusMeta: Record<string, { label: string; variant: 'success' | 'info' | 'warn' }> = {
    ready: { label: 'Ready', variant: 'success' },
    running: { label: 'Tools starting', variant: 'warn' },
    provisioning: { label: 'Provisioning', variant: 'info' },
  };
  const meta = statusMeta[active.status] ?? { label: 'Provisioning', variant: 'info' as const };
  return (
    <>
      <Card mb="md">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Badge variant={meta.variant}>{meta.label}</Badge>
            <Stack gap={0}>
              <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                {active.lab_name}
              </Text>
              {minsLeft !== null ? (
                <Group gap={4}>
                  <Clock size={11} color="rgb(var(--c-txt-muted))" />
                  <Text size="xs" c={minsLeft <= 5 ? 'red' : 'dimmed'}>
                    {minsLeft}m until auto-destroy
                  </Text>
                </Group>
              ) : null}
            </Stack>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<TerminalSquare size={14} />}
              onClick={() => navigate({ to: '/labs/$id', params: { id: active.lab_slug } })}
            >
              Resume
            </Button>
            <Button
              variant="danger"
              size="sm"
              leftIcon={<Square size={14} />}
              loading={stopLab.isPending}
              onClick={() => setConfirmStop(true)}
            >
              Stop
            </Button>
          </Group>
        </Group>
      </Card>

      {/* Stopping destroys the running lab VM and any unsaved work in it — it
          used to fire on a single unguarded click. */}
      <ConfirmDialog
        opened={confirmStop}
        onClose={() => setConfirmStop(false)}
        onConfirm={() => {
          stopLab.mutate({ slug: active.lab_slug });
          setConfirmStop(false);
        }}
        title="Stop this lab?"
        confirmLabel="Stop lab"
        loading={stopLab.isPending}
      >
        {`"${active.lab_name}" will be torn down and its VM destroyed. Progress you have already submitted is kept; anything unsaved inside the desktop is lost.`}
      </ConfirmDialog>
    </>
  );
}

function TraineeLabsView() {
  const assignments = useMyAssignments();
  const labs = useLabs();
  // Join assignments (name/due/required) with the catalog (difficulty/tool/
  // points) so a trainee's cards look identical to the staff catalog. Falls
  // back to a minimal card if the lab isn't in the readable catalog.
  const bySlug = useMemo(() => {
    const m = new Map<string, LabSummary>();
    for (const l of labs.data ?? []) m.set(l.slug, l);
    return m;
  }, [labs.data]);
  const items = useMemo(
    () =>
      (assignments.data ?? []).map((a) => ({
        assignment: a,
        lab:
          bySlug.get(a.lab_slug) ??
          ({
            id: String(a.lab_id),
            slug: a.lab_slug,
            title: a.lab_name,
            description: '',
            scenarioSlug: '',
            difficulty: 'beginner' as Difficulty,
            estimatedMins: 0,
            taskCount: 0,
          } satisfies LabSummary),
      })),
    [assignments.data, bySlug],
  );
  const loading = assignments.isPending || labs.isPending;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={FlaskConical}
        title="Labs"
        description="The labs your instructor has assigned to you. Open one to launch its desktop."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Labs' }]}
      />
      <ActiveLabBanner />
      {loading ? (
        <Grid gutter="md">
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6, lg: 4 }}>
              <Skeleton height={180} radius="md" />
            </Grid.Col>
          ))}
        </Grid>
      ) : items.length === 0 ? (
        <EmptyState
          illustration={<FlaskConical size={40} />}
          title="No labs assigned yet"
          description="When your instructor assigns a lab to your cohort, it appears here. Check back soon."
        />
      ) : (
        <motion.div variants={staggerContainer} initial="hidden" animate="show">
          <Stack gap="sm">
            {items.map(({ assignment, lab }) => (
              <LabRow
                key={assignment.lab_id}
                lab={lab}
                assignment={{ dueAt: assignment.due_at, required: assignment.required }}
              />
            ))}
          </Stack>
        </motion.div>
      )}
    </PageContainer>
  );
}

function LabsIndex() {
  const isTrainee = useAuthStore(selectRole) === 'trainee';
  const { data, isPending, isError, error, refetch } = useLabs();
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [difficulties, setDifficulties] = useState<ReadonlySet<Difficulty>>(() => new Set());
  const [categories, setCategories] = useState<ReadonlySet<string>>(() => new Set());
  const [tools, setTools] = useState<ReadonlySet<string>>(() => new Set());
  const [sort, setSort] = useState<'name' | 'difficulty' | 'points'>('name');

  // Distinct categories + tools present in the catalog, with per-value counts,
  // for the checkbox filter groups.
  const categoryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of data ?? []) if (l.category) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
    return [...counts.keys()].sort().map((c) => ({ value: c, label: CATEGORY_LABEL[c] ?? categoryLabel(c), count: counts.get(c) ?? 0 }));
  }, [data]);
  const toolOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const l of data ?? []) if (l.amiRole) counts.set(l.amiRole, (counts.get(l.amiRole) ?? 0) + 1);
    return [...counts.keys()].map((r) => ({ value: r, label: toolLabel(r), count: counts.get(r) ?? 0 }))
      .filter((o, i, a) => a.findIndex((x) => x.label === o.label) === i)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = deferredSearch.trim().toLowerCase();
    return data.filter((lab) => {
      if (difficulties.size > 0 && !difficulties.has(lab.difficulty)) return false;
      if (categories.size > 0 && !(lab.category && categories.has(lab.category))) return false;
      if (tools.size > 0 && !(lab.amiRole && tools.has(lab.amiRole))) return false;
      if (!q) return true;
      return (
        lab.title.toLowerCase().includes(q) ||
        lab.description.toLowerCase().includes(q) ||
        categoryLabel(lab.category).toLowerCase().includes(q) ||
        toolLabel(lab.amiRole).toLowerCase().includes(q)
      );
    });
  }, [data, deferredSearch, difficulties, categories, tools]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    if (sort === 'name') rows.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === 'points') rows.sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
    else rows.sort((a, b) => DIFFICULTY_ORDER.indexOf(a.difficulty) - DIFFICULTY_ORDER.indexOf(b.difficulty));
    return rows;
  }, [filtered, sort]);

  const toggleIn = <T,>(set: (fn: (p: ReadonlySet<T>) => ReadonlySet<T>) => void, key: T) =>
    set((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const activeFilterCount = difficulties.size + categories.size + tools.size + (search.trim() ? 1 : 0);

  const clearFilters = () => {
    setSearch('');
    setDifficulties(new Set());
    setCategories(new Set());
    setTools(new Set());
  };

  if (isTrainee) return <TraineeLabsView />;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={FlaskConical}
        title="Labs"
        description="Hands-on lab exercises you can launch on demand."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Labs' }]}
      />

      <ActiveLabBanner />

      {isError ? (
        <ErrorState title="Could not load labs" error={error} onRetry={() => void refetch()} />
      ) : (
        <Grid gutter="lg">
          {/* Filter rail */}
          <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
            <div style={{ position: 'sticky', top: 16 }}>
              <Stack gap="md">
                <TextInput
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  placeholder="Search labs…"
                  leftSection={<Search size={14} />}
                  aria-label="Search labs"
                />
                <Card>
                  <Stack gap="lg">
                    <Group justify="space-between">
                      <Text size="sm" fw={700}>Filters</Text>
                      {activeFilterCount > 0 ? (
                        <UnstyledButton onClick={clearFilters}>
                          <Text size="xs" c="rgb(var(--c-neon))">Reset</Text>
                        </UnstyledButton>
                      ) : null}
                    </Group>
                    <FilterGroup title="Difficulty">
                      {DIFFICULTY_CHIPS.map((c) => (
                        <CheckRow key={c.key} label={c.label} checked={difficulties.has(c.key)} onToggle={() => toggleIn(setDifficulties, c.key)} />
                      ))}
                    </FilterGroup>
                    <FilterGroup title="Category">
                      {categoryOptions.map((o) => (
                        <CheckRow key={o.value} label={o.label} count={o.count} checked={categories.has(o.value)} onToggle={() => toggleIn(setCategories, o.value)} />
                      ))}
                    </FilterGroup>
                    <FilterGroup title="Tool">
                      {toolOptions.map((o) => (
                        <CheckRow key={o.value} label={o.label} count={o.count} checked={tools.has(o.value)} onToggle={() => toggleIn(setTools, o.value)} />
                      ))}
                    </FilterGroup>
                  </Stack>
                </Card>
              </Stack>
            </div>
          </Grid.Col>

          {/* Results */}
          <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
            <Group justify="space-between" mb="md" wrap="wrap">
              <Text size="sm" c="dimmed">
                {isPending ? 'Loading…' : `${sorted.length} of ${data?.length ?? 0} labs`}
              </Text>
              <Select
                data={[
                  { value: 'name', label: 'Sort: Name' },
                  { value: 'difficulty', label: 'Sort: Difficulty' },
                  { value: 'points', label: 'Sort: Points' },
                ]}
                value={sort}
                onChange={(v) => setSort((v as 'name' | 'difficulty' | 'points') ?? 'name')}
                allowDeselect={false}
                style={{ width: 180 }}
                aria-label="Sort labs"
              />
            </Group>
            {isPending ? (
              <Stack gap="sm">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height={92} radius="md" />
                ))}
              </Stack>
            ) : (data?.length ?? 0) === 0 ? (
              <EmptyState
                illustration={<TerminalSquare size={48} />}
                title="No labs yet"
                description="Your org admin can publish labs for your tenant."
              />
            ) : sorted.length === 0 ? (
              <EmptyState
                illustration={<FlaskConical size={48} />}
                title="No labs match"
                description="Try clearing a filter or adjusting your search."
                cta={{ label: 'Clear filters', onClick: clearFilters, variant: 'secondary' }}
              />
            ) : (
              <motion.div variants={staggerContainer} initial="hidden" animate="show">
                <Stack gap="sm">
                  {sorted.map((lab) => (
                    <LabRow key={lab.id} lab={lab} />
                  ))}
                </Stack>
              </motion.div>
            )}
          </Grid.Col>
        </Grid>
      )}
    </PageContainer>
  );
}


function FilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap={6}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed">{title}</Text>
      <Stack gap={2}>{children}</Stack>
    </Stack>
  );
}

function CheckRow({ label, count, checked, onToggle }: { label: string; count?: number; checked: boolean; onToggle: () => void }) {
  return (
    <UnstyledButton
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', width: '100%' }}
    >
      <Checkbox checked={checked} onChange={() => {}} size="xs" tabIndex={-1} aria-hidden styles={{ input: { cursor: 'pointer' } }} />
      <Text size="sm" c={checked ? 'rgb(var(--c-txt-bright))' : 'rgb(var(--c-txt))'} style={{ flex: 1, textAlign: 'left' }}>
        {label}
      </Text>
      {typeof count === 'number' ? <Text size="xs" c="dimmed" className="font-mono">{count}</Text> : null}
    </UnstyledButton>
  );
}
